import type {
  BulkClassStudentUpdateModel,
  Class,
  ClassSummary,
  ClinicalGroup,
  ClinicalGroupSummary,
  Leaderboard,
  LeaderboardSkillsDetail,
  PreparedImportFile,
  SvelteFetch,
} from '$lib/api/types';
import { apiFetchRaw, fetchJson, getJson } from '$lib/api/fetch';

export async function listCurrentClasses(svelteFetch: SvelteFetch): Promise<ClassSummary[]> {
  return await getJson(`/classes/current`, undefined, svelteFetch);
}

export async function listPastClasses(page: number, pageSize = 20): Promise<ClassSummary[]> {
  return await getJson(`/classes/past?page=${page}&pageSize=${pageSize}`);
}

export async function quickSearchCurrentClasses(
  search: string,
  page: number,
  pageSize = 10,
  signal: AbortSignal,
): Promise<Pick<Class, 'id' | 'name' | 'courseId' | 'course'>[]> {
  return await getJson(`/classes/quicksearch/current?search=${search}&page=${page}&pageSize=${pageSize}`, signal);
}

export async function createClass(data: Pick<Class, 'name' | 'startDate' | 'endDate' | 'courseId'>): Promise<Class> {
  return await fetchJson('/classes', 'POST', data);
}

export async function getClass(id: number, svelteFetch: SvelteFetch): Promise<Class> {
  return await getJson(`/classes/${id}`, undefined, svelteFetch);
}

export async function updateClass(
  id: number,
  data: Pick<Class, 'name' | 'startDate' | 'endDate' | 'courseId'>,
): Promise<Class> {
  return await fetchJson(`/classes/${id}`, 'PUT', data);
}

export async function deleteClass(id: number): Promise<Class> {
  return await fetchJson(`/classes/${id}`, 'DELETE', undefined);
}

export async function listClinicalGroups(classId: number, svelteFetch?: SvelteFetch): Promise<ClinicalGroupSummary[]> {
  return await getJson(`/classes/${classId}/clinicalgroups`, undefined, svelteFetch);
}

export async function createClinicalGroup(
  classId: number,
  clinicalGroup: Pick<ClinicalGroup, 'name' | 'instructorId'>,
): Promise<ClinicalGroup> {
  return await fetchJson(`/classes/${classId}/clinicalgroup`, 'POST', clinicalGroup);
}

export async function updateClinicalGroup(
  classId: number,
  clinicalGroupId: number,
  clinicalGroup: { name?: string; instructorId?: number },
): Promise<ClinicalGroup> {
  return await fetchJson(`/classes/${classId}/clinicalgroup/${clinicalGroupId}`, 'PATCH', clinicalGroup);
}

export async function deleteClinicalGroup(classId: number, clinicalGroupId: number): Promise<ClinicalGroup> {
  return await fetchJson(`/classes/${classId}/clinicalgroup/${clinicalGroupId}`, 'DELETE', undefined);
}

export async function listStudents(
  classId: number,
  search: string,
  page: number,
  pageSize = 20,
  signal?: AbortSignal,
): Promise<Leaderboard[]> {
  return await getJson(`/classes/${classId}/students?${search}&page=${page}&pageSize=${pageSize}`, signal);
}

export async function addStudent(classId: number, studentId: number, clinicalGroupId?: number): Promise<Leaderboard> {
  return await fetchJson(`/classes/${classId}/students`, 'POST', { userId: studentId, clinicalGroupId });
}

export async function studentClassDetails(classId: number, userId: number): Promise<LeaderboardSkillsDetail> {
  return await getJson(`/classes/${classId}/students/${userId}`);
}

export async function removeStudent(classId: number, studentId: number): Promise<void> {
  await fetchJson<undefined, undefined>(`/classes/${classId}/students/${studentId}`, 'DELETE', undefined, true);
}

export async function updateStudent(classId: number, userId: number, clinicalGroupId?: number): Promise<Leaderboard> {
  return await fetchJson(`/classes/${classId}/students/${userId}`, 'PUT', { clinicalGroupId });
}

export async function studentBulkAction(
  classId: number,
  bulkModel: BulkClassStudentUpdateModel,
): Promise<{ affectedRows: number }> {
  return await fetchJson(`/classes/${classId}/students/bulk`, 'POST', bulkModel);
}

export async function prepareStudentImport(classId: number, file: File): Promise<PreparedImportFile> {
  const formData = new FormData();
  formData.append('file', file);
  return await apiFetchRaw(`/classes/${classId}/preparestudentimport`, 'POST', formData);
}

export async function confirmStudentImport(classId: number, operationId: string): Promise<void> {
  await fetchJson(`/classes/${classId}/confirmimport/${operationId}`, 'POST', undefined, true);
}

export async function resetLeaderboard(classId: number): Promise<void> {
  await fetchJson(`/classes/${classId}/resetleaderboard`, 'POST', undefined, true);
}
